import { Fragment, useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../../lib/api';
import type { PhysicalAsset } from '../../lib/types';
import { SearchSelect } from '../../components/SearchSelect';
import { Pill, Spinner, Field, TextInput, TextArea, DateInput, FormGrid, NumberInput } from '../../components/ui';
import { ItemReworkPanel, BatchApplyOperationsForm } from './ItemReworkPanel';
import { ReworkDeliveryPanel } from './ReworkDeliveryPanel';
import { AddItemPanel } from './AddItemPanel';

// 返修批次详情（列表内联展开）：基本信息（可编辑）+ 工件与来源（可追加/移除/换绑）+ 来源确认 + 单件返工执行。
// 阶段 1：来源确认；阶段 2：工序/粉末耗用/本次返工检验；阶段 3：交货（不含费用）。
// 工件可编辑边界：仅 status ∈ draft/pending_source 可移除/换绑；已执行/已检验/已交货由后端 409 item_has_records 兜底。

type BatchItem = {
  id: string;
  asset_id?: string | null;
  customer_serial?: string | null;
  internal_code?: string | null;
  internal_identifier?: string | null;
  original_delivery_number?: string | null;
  original_contract_number?: string | null;
  original_inspection_number?: string | null;
  /** 返工质检单号：新检验放行后由后端带回（兼容多种字段命名，按实际适配）。 */
  rework_inspection_number?: string | null;
  latest_rework_inspection_number?: string | null;
  released_inspection_number?: string | null;
  rework_qc_number?: string | null;
  rework_inspection?: { id?: string; number?: string | null } | null;
  latest_inspection?: { id?: string; number?: string | null } | null;
  item_code?: string | null;
  item_name_zh?: string | null;
  service_cycle_version?: number | null;
  quantity?: number;
  draft_bulk?: number;
  source_verified?: number;
  status?: string;
  problem?: string | null;
};
type BatchDetail = {
  id: string;
  number: string;
  customer_id?: string | null;
  customer_name?: string | null;
  customer_code?: string | null;
  customer_repair_number?: string | null;
  receipt_date?: string;
  reason?: string;
  expected_completion_date?: string | null;
  notes?: string | null;
  status?: string;
  items: BatchItem[];
  summary?: { item_count: number; draft_count: number; confirmed_count: number };
};

const reworkQcNumber = (i: BatchItem): string =>
  i.rework_inspection_number
  || i.latest_rework_inspection_number
  || i.released_inspection_number
  || i.rework_qc_number
  || i.rework_inspection?.number
  || i.latest_inspection?.number
  || '';

// 本次返工检验行（GET /api/rework-batches/items/:id/inspections）：用于展示放行后的返工质检单号。
type ItemInspection = {
  id: string;
  number: string;
  release_state?: string | null;
  inspection_round?: number | null;
  inspection_date?: string | null;
  original_inspection_number?: string | null;
};

const ITEM_STATUS: Record<string, { label: string; tone: 'ok' | 'info' | 'warn' | 'neutral' | 'bad' }> = {
  draft: { label: '草稿', tone: 'neutral' },
  pending_source: { label: '待核实来源', tone: 'warn' },
  rework: { label: '返工中', tone: 'info' },
  inspecting: { label: '检验中', tone: 'info' },
  qualified: { label: '合格待交', tone: 'ok' },
  delivered: { label: '已交货', tone: 'ok' },
  rejected: { label: '不合格', tone: 'bad' },
  cancelled: { label: '已取消', tone: 'neutral' },
};
const BATCH_STATUS: Record<string, { label: string; tone: 'ok' | 'info' | 'warn' | 'neutral' | 'bad' }> = {
  draft: { label: '草稿', tone: 'neutral' },
  received: { label: '已收件', tone: 'info' },
  in_progress: { label: '返工中', tone: 'info' },
  pending_inspection: { label: '待检验', tone: 'warn' },
  pending_delivery: { label: '待交货', tone: 'warn' },
  partially_delivered: { label: '部分交货', tone: 'ok' },
  completed: { label: '已完成', tone: 'ok' },
  cancelled: { label: '已取消', tone: 'neutral' },
};

// 409 item_has_records 的 details.counts → 中文摘要；兼容 details.counts 与 details 直挂两种形态。
function countsText(details: unknown): string {
  if (!details || typeof details !== 'object') return '';
  const obj = details as Record<string, unknown>;
  const src = (obj.counts && typeof obj.counts === 'object' ? obj.counts : obj) as Record<string, unknown>;
  const labels: Array<[string, string]> = [
    ['rework_operations', '返工工序'],
    ['quality_inspections', '检验记录'],
    ['delivery_lines', '交货记录'],
    ['asset_movements', '工件流水'],
  ];
  const seen = new Set<string>();
  const parts: string[] = [];
  for (const [key, label] of labels) {
    if (seen.has(label)) continue;
    const v = src[key];
    if (typeof v === 'number' && v > 0) { parts.push(`${label} ${v}`); seen.add(label); }
  }
  return parts.join(' · ');
}

function itemGuardMessage(e: unknown, action: string): string {
  if (e instanceof ApiError) {
    if (e.status === 409 && (e.code === 'item_has_records' || /item_has_records/.test(e.message || ''))) {
      const c = countsText(e.details);
      return `已执行/已检验/已交货的工件不可${action}${c ? `（${c}）` : ''}`;
    }
    return e.message || `${action}失败`;
  }
  return e instanceof Error ? e.message : `${action}失败`;
}

export function ReworkBatchDetailPanel({ id, canWrite, onChanged }: { id: string; canWrite: boolean; onChanged: () => void }) {
  const [data, setData] = useState<BatchDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  // 批次基本信息内联编辑
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    customer_repair_number: '', receipt_date: '', reason: '', expected_completion_date: '', notes: '',
  });
  // 添加工件面板
  const [addingItems, setAddingItems] = useState(false);
  // 工件换绑
  const [rebindId, setRebindId] = useState<string | null>(null);
  const [rebindForm, setRebindForm] = useState({ assetId: '', assetLabel: '', identifier: '', quantity: '1', problem: '' });
  // 返工质检单号（新检验放行后显示）：items 列表不直接带该字段，按工件拉取本次检验清单取最新已放行单号。
  const [reworkQc, setReworkQc] = useState<Record<string, string>>({});

  const load = useCallback(() => {
    setLoading(true);
    setErr('');
    (async () => {
      try {
        const d = await api.detail<BatchDetail>(`/api/rework-batches/${encodeURIComponent(id)}`);
        setData(d);
        // 仅对已确认来源/已开始执行的工件拉取本次返工检验（草稿行没有检验，省请求）。
        const items = d.items || [];
        const targets = items.filter((i) => Number(i.source_verified) === 1 || !['draft', 'pending_source'].includes(i.status || ''));
        const lists = await Promise.all(targets.map((i) =>
          api.list<ItemInspection>(`/api/rework-batches/items/${encodeURIComponent(i.id)}/inspections`)
            .catch(() => [] as ItemInspection[])));
        const map: Record<string, string> = {};
        targets.forEach((it, idx) => {
          const released = lists[idx]
            .filter((r) => r.release_state === 'released')
            .sort((a, b) => (Number(b.inspection_round) || 0) - (Number(a.inspection_round) || 0));
          if (released.length) map[it.id] = released[0].number;
        });
        setReworkQc(map);
      } catch (e: any) {
        setErr(e?.message || '加载失败');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);
  useEffect(load, [load]);

  // 子面板动作后：重载本批次详情并刷新交货面板（其可交付清单随工件放行变化），
  // 同时通知外层列表。避免出现「已放行但交货面板仍显示无可交付」的陈旧状态。
  const handleChanged = useCallback(() => {
    load();
    setRefreshKey((k) => k + 1);
    onChanged();
  }, [load, onChanged]);

  const confirmSource = async () => {
    setBusy(true);
    setErr('');
    setMsg('');
    try {
      const out: any = await api.post(`/api/rework-batches/${encodeURIComponent(id)}/confirm-source`, {});
      setMsg(out?.message || '来源确认完成');
      load();
      onChanged();
    } catch (e: any) {
      setErr(e?.message || '来源确认失败');
    } finally {
      setBusy(false);
    }
  };

  const openEdit = () => {
    if (!data) return;
    setEditForm({
      customer_repair_number: data.customer_repair_number || '',
      receipt_date: data.receipt_date || '',
      reason: data.reason || '',
      expected_completion_date: data.expected_completion_date || '',
      notes: data.notes || '',
    });
    setEditing(true);
    setAddingItems(false);
    setRebindId(null);
    setErr('');
    setMsg('');
  };

  const saveEdit = async () => {
    if (!editForm.reason.trim()) { setErr('返工原因为必填'); return; }
    if (!editForm.receipt_date) { setErr('到货日期为必填'); return; }
    setBusy(true);
    setErr('');
    setMsg('');
    try {
      // PATCH /api/rework-batches/:id：仅下发可编辑的 5 个字段，其它字段由后端保留。
      const out: any = await api.patch(`/api/rework-batches/${encodeURIComponent(id)}`, {
        customer_repair_number: editForm.customer_repair_number.trim(),
        receipt_date: editForm.receipt_date,
        reason: editForm.reason.trim(),
        expected_completion_date: editForm.expected_completion_date || '',
        notes: editForm.notes.trim(),
      });
      const d = out?.data ?? out;
      setMsg(d?.message || '返修批次已更新');
      setEditing(false);
      load();
      onChanged();
    } catch (e: any) {
      setErr(e?.message || '保存失败');
    } finally {
      setBusy(false);
    }
  };

  const removeItem = async (item: BatchItem) => {
    const label = item.customer_serial || item.internal_identifier || item.internal_code || item.id;
    const started = ['inspecting', 'qualified', 'rejected', 'delivered'].includes(item.status || '');
    const tip = started
      ? '该工件已开始返工/检验/交货，会被拒绝。'
      : '仅尚未开工的工件可移除；已执行/已检验/已交货的工件会被拒绝。';
    if (!window.confirm(`确认移除工件「${label}」？${tip}`)) return;
    setBusy(true);
    setErr('');
    setMsg('');
    try {
      await api.del(`/api/rework-batches/items/${encodeURIComponent(item.id)}`);
      setMsg('工件已移除');
      setRebindId(null);
      load();
      onChanged();
    } catch (e: any) {
      setErr(itemGuardMessage(e, '移除'));
    } finally {
      setBusy(false);
    }
  };

  const openRebind = (item: BatchItem) => {
    setRebindId(item.id);
    setRebindForm({
      assetId: '',
      assetLabel: '',
      identifier: item.internal_identifier || '',
      quantity: String(item.quantity ?? 1),
      problem: item.problem || '',
    });
    setErr('');
    setMsg('');
  };

  const saveRebind = async (item: BatchItem) => {
    const body: Record<string, unknown> = {};
    if (rebindForm.assetId) body.asset_id = rebindForm.assetId;
    const identifier = rebindForm.identifier.trim();
    if (identifier && identifier !== (item.internal_identifier || '')) body.internal_identifier = identifier;
    const qty = Number(rebindForm.quantity);
    if (!(qty > 0)) { setErr('数量需大于 0'); return; }
    if (qty !== Number(item.quantity ?? 1)) body.quantity = qty;
    if (rebindForm.problem.trim() !== (item.problem || '')) body.problem = rebindForm.problem.trim();
    if (!Object.keys(body).length) { setErr('没有变化：请选择要绑定的现有工件，或填写新的内部识别号/数量/问题'); return; }
    setBusy(true);
    setErr('');
    setMsg('');
    try {
      await api.patch(`/api/rework-batches/items/${encodeURIComponent(item.id)}`, body);
      setMsg('工件已换绑/更新');
      setRebindId(null);
      load();
      onChanged();
    } catch (e: any) {
      setErr(itemGuardMessage(e, '换绑'));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Spinner label="正在读取批次详情…" />;
  if (err && !data) return <div className="notice bad">{err}</div>;
  if (!data) return null;

  const confirmable = data.items.filter((i) => i.status === 'draft' || i.status === 'pending_source');
  const batchEditable = canWrite && !['cancelled', 'completed'].includes(data.status || '');
  const bs = BATCH_STATUS[data.status || 'draft'] || BATCH_STATUS.draft;

  return (
    <div className="k" style={{ padding: '4px 0 8px' }}>
      <div style={{ marginBottom: 6 }}>
        <span className="strong">{data.number}</span>
        <span className="muted"> · 客户 {data.customer_name || data.customer_code || '—'}</span>
        {data.customer_repair_number ? <span className="muted"> · 客户返修单号 {data.customer_repair_number}</span> : null}
        <span className="muted"> · 到货 {data.receipt_date || '—'}</span>
        <span className="muted"> · 原因 {data.reason || '—'}</span>
        <span style={{ marginLeft: 6 }}><Pill tone={bs.tone} label={bs.label} /></span>
        {data.expected_completion_date ? <span className="muted"> · 预计完成 {data.expected_completion_date}</span> : null}
        {data.notes ? <span className="muted"> · 备注 {data.notes}</span> : null}
      </div>
      {err ? <div className="notice bad">{err}</div> : null}
      {msg ? <div className="notice">{msg}</div> : null}

      {batchEditable ? (
        <div className="detail-actions" style={{ marginBottom: 6, flexWrap: 'wrap', gap: 6 }}>
          <button className="btn" onClick={() => (editing ? setEditing(false) : openEdit())}>
            {editing ? '收起编辑' : '编辑批次'}
          </button>
          <button className="btn" onClick={() => { setAddingItems((v) => !v); setEditing(false); setRebindId(null); }}>
            {addingItems ? '收起添加工件' : '添加工件'}
          </button>
        </div>
      ) : null}

      {editing ? (
        <div className="panel" style={{ margin: '6px 0', padding: 12 }}>
          <div className="strong" style={{ marginBottom: 6 }}>编辑批次信息</div>
          <FormGrid>
            <Field label="客户返修单号">
              <TextInput value={editForm.customer_repair_number} onChange={(e) => setEditForm((s) => ({ ...s, customer_repair_number: e.target.value }))} placeholder="客户侧返修单号（可选）" />
            </Field>
            <Field label="到货日期 *" required>
              <DateInput value={editForm.receipt_date} onChange={(e) => setEditForm((s) => ({ ...s, receipt_date: e.target.value }))} />
            </Field>
            <Field label="预计完成日期">
              <DateInput value={editForm.expected_completion_date} onChange={(e) => setEditForm((s) => ({ ...s, expected_completion_date: e.target.value }))} />
            </Field>
            <Field label="返工原因 *" required span>
              <TextArea value={editForm.reason} onChange={(e) => setEditForm((s) => ({ ...s, reason: e.target.value }))} placeholder="本批次统一的返工原因…" />
            </Field>
            <Field label="备注" span>
              <TextArea value={editForm.notes} onChange={(e) => setEditForm((s) => ({ ...s, notes: e.target.value }))} />
            </Field>
          </FormGrid>
          <div className="detail-actions" style={{ marginTop: 8 }}>
            <button className="btn primary" disabled={busy} onClick={() => void saveEdit()}>{busy ? '保存中…' : '保存批次'}</button>
            <button className="btn" disabled={busy} onClick={() => setEditing(false)}>取消</button>
          </div>
        </div>
      ) : null}

      <table className="grid" style={{ marginTop: 4 }}>
        <thead>
          <tr>
            <th>工件 / 内部识别号</th>
            <th>物料</th>
            <th>原交货单</th>
            <th>原合同</th>
            <th>原检验</th>
            <th>返工质检</th>
            <th style={{ textAlign: 'right' }}>数量</th>
            <th>履历版本</th>
            <th>状态</th>
            <th>本次问题</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {data.items.map((i) => {
            const s = ITEM_STATUS[i.status || 'draft'] || ITEM_STATUS.draft;
            const canExecute = canWrite && (i.asset_id != null || ['rework', 'inspecting', 'qualified', 'rejected'].includes(i.status || ''));
            // 可移除：草稿/待核实来源；以及已确认来源但尚未开工的 rework（后端 409 兜底：
            // 已有返工/检验/交货/收费或收件以外流水时仍会被拒绝）。
            const started = ['inspecting', 'qualified', 'rejected', 'delivered'].includes(i.status || '');
            const itemEditable = canWrite && ['draft', 'pending_source', 'rework'].includes(i.status || '') && !started;
            const open = expanded === i.id;
            const label = i.customer_serial || i.internal_identifier || i.internal_code || i.id;
            const qcNo = reworkQcNumber(i) || reworkQc[i.id] || '';
            return (
              <Fragment key={i.id}>
                <tr>
                  <td className="muted">{i.customer_serial || i.internal_identifier || i.internal_code || '—'}</td>
                  <td className="muted">{i.item_name_zh || i.item_code || '—'}</td>
                  <td className="muted">{i.original_delivery_number || '—'}</td>
                  <td className="muted">{i.original_contract_number || '—'}</td>
                  <td className="muted">{i.original_inspection_number || '—'}</td>
                  <td className="muted">
                    {qcNo ? (
                      <span title={`返工质检单号 ${qcNo}${i.original_inspection_number ? ` · 关联原检验 ${i.original_inspection_number}` : ''}`}>
                        返工质检单号 {qcNo}
                      </span>
                    ) : '—'}
                  </td>
                  <td style={{ textAlign: 'right' }}>{i.quantity ?? 1}</td>
                  <td className="muted">{i.service_cycle_version ? `V${i.service_cycle_version}` : '—'}</td>
                  <td><Pill tone={s.tone} label={s.label} /></td>
                  <td className="muted">{i.problem || '—'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {canExecute ? (
                        <button className="btn" onClick={() => { setExpanded(open ? null : i.id); setRebindId(null); }}>{open ? '收起' : '返工执行'}</button>
                      ) : null}
                      {itemEditable ? (
                        <>
                          <button className="btn" disabled={busy} onClick={() => (rebindId === i.id ? setRebindId(null) : openRebind(i))}>
                            {rebindId === i.id ? '收起' : '换绑'}
                          </button>
                          <button className="btn" disabled={busy} onClick={() => void removeItem(i)}>移除</button>
                        </>
                      ) : canWrite ? (
                        <span
                          className="muted"
                          style={{ fontSize: 12 }}
                          title="已开始返工/检验/交货的工件不可移除/换绑：已有执行/检验/交货记录（后端 409 item_has_records）"
                        >
                          已开工 · 不可移除/换绑
                        </span>
                      ) : null}
                    </div>
                  </td>
                </tr>
                {rebindId === i.id ? (
                  <tr>
                    <td colSpan={11} style={{ padding: 0 }}>
                      <div className="panel" style={{ margin: '6px 0', padding: 12, background: 'var(--surface-2, #f7f7f7)' }}>
                        <div className="strong" style={{ marginBottom: 6 }}>换绑 / 修改工件「{label}」</div>
                        <FormGrid>
                          <Field label="绑定现有工件（可选）" hint="按序列号检索该客户已有工件；留空则仅改内部识别号/数量/问题">
                            <SearchSelect<PhysicalAsset>
                              endpoint="/api/physical-assets"
                              params={data.customer_id ? { customer_id: data.customer_id } : undefined}
                              value={rebindForm.assetId || null}
                              label={rebindForm.assetLabel}
                              optionTitle={(a) => `${a.customer_serial}${a.internal_code ? ` · ${a.internal_code}` : ''}`}
                              optionMeta={(a) => a.item_code || ''}
                              placeholder="检索现有工件…"
                              onChange={(a) => setRebindForm((s) => ({ ...s, assetId: a?.id || '', assetLabel: a ? a.customer_serial : '' }))}
                            />
                          </Field>
                          <Field label="内部识别号 / 序列号">
                            <TextInput value={rebindForm.identifier} onChange={(e) => setRebindForm((s) => ({ ...s, identifier: e.target.value }))} />
                          </Field>
                          <Field label="数量">
                            <NumberInput min={1} value={rebindForm.quantity} onChange={(e) => setRebindForm((s) => ({ ...s, quantity: e.target.value }))} />
                          </Field>
                          <Field label="问题">
                            <TextInput value={rebindForm.problem} onChange={(e) => setRebindForm((s) => ({ ...s, problem: e.target.value }))} />
                          </Field>
                        </FormGrid>
                        <div className="detail-actions" style={{ marginTop: 8 }}>
                          <button className="btn primary" disabled={busy} onClick={() => void saveRebind(i)}>保存换绑</button>
                          <button className="btn" disabled={busy} onClick={() => setRebindId(null)}>取消</button>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : null}
                {open ? (
                  <tr key={`${i.id}-panel`}>
                    <td colSpan={11} style={{ padding: 0 }}>
                      <ItemReworkPanel
                        itemId={i.id}
                        itemLabel={i.customer_serial || i.internal_identifier || i.internal_code || data.number}
                        canWrite={canWrite}
                        onChanged={handleChanged}
                      />
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            );
          })}
        </tbody>
      </table>

      {addingItems ? (
        <AddItemPanel
          batchId={data.id}
          customerId={data.customer_id}
          onChanged={handleChanged}
          onAdded={(m) => setMsg(m)}
          onClose={() => setAddingItems(false)}
        />
      ) : null}

      <BatchApplyOperationsForm batchId={data.id} canWrite={canWrite} onChanged={handleChanged} />

      {data.items.some((i) => ['qualified', 'delivered'].includes(i.status || '')) || canWrite ? (
        <ReworkDeliveryPanel key={refreshKey} batchId={data.id} canWrite={canWrite} onChanged={handleChanged} />
      ) : null}

      {canWrite && confirmable.length > 0 ? (
        <div className="detail-actions" style={{ marginTop: 8 }}>
          <button className="btn primary" onClick={confirmSource} disabled={busy}>
            {busy ? '确认中…' : `确认来源（${confirmable.length} 件，拆单件并建内部工件身份）`}
          </button>
        </div>
      ) : null}
    </div>
  );
}
